/**
 * useZoomHandlers - Zoom Interaction Hook
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Handles all zoom-related interactions and state management.
 * Separates zoom logic from chart rendering and data processing concerns.
 * 
 * Responsibilities:
 * - Manage zoom state (isZoomed, zoomLevel)
 * - Handle mouse events for zoom selection
 * - Configure zoom controller callbacks
 * - Provide zoom reset functionality
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { ZoomController } from '../components/ZoomController.js';

export function useZoomHandlers({ originalDataRef, chartData }) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [dragState, setDragState] = useState({ dragging: false, left: '', right: '' });
  const zoomController = useRef(new ZoomController());

  // === Zoom Handlers ===
  const handleZoomChange = useCallback((zoomData) => {
    setIsZoomed(zoomData.isZoomed);
    setZoomLevel(zoomData.zoomLevel || 100);
    return zoomData.data; // Return the zoomed data
  }, []);

  const handleMouseDown = useCallback((e) => {
    zoomController.current.handleMouseDown(e);
  }, []);

  const handleMouseMove = useCallback((e) => {
    zoomController.current.handleMouseMove(e);
  }, []);

  const handleMouseUp = useCallback(() => {
    zoomController.current.handleMouseUp();
  }, []);

  const handleResetZoom = useCallback(() => {
    zoomController.current.resetZoom();
  }, []);

  // Configure zoom controller callback
  useEffect(() => {
    zoomController.current.setZoomChangeCallback(handleZoomChange);
  }, [handleZoomChange]);

  // Configure drag state callback for real-time updates
  useEffect(() => {
    const handleDragStateChange = (newDragState) => {
      setDragState(newDragState);
    };
    
    zoomController.current.setDragStateChangeCallback(handleDragStateChange);
  }, []);

  // Configure zoom controller with original data
  useEffect(() => {
    if (originalDataRef.current && originalDataRef.current.length > 0) {
      console.log('🔧 Setting original data in zoom controller:', originalDataRef.current.length, 'points');
      zoomController.current.setOriginalData(originalDataRef.current);
    }
  }, [chartData]); // Use chartData as dependency so it runs when data changes

  return {
    isZoomed,
    zoomLevel,
    zoomController: zoomController.current,
    dragState,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleResetZoom,
    handleZoomChange
  };
}

export default useZoomHandlers;